// Ranked seasons (design: mythic-idle-design/pvp-depth-proposal.md, "Ranked seasons" and "Earned odds boosts").
// Behind an admin switch, on by default (season one): while it is off the weekly Arena (shared/arena.ts) runs exactly
// as before. While it is on, the Arena page plays this ladder instead. Pure rules shared by the server (server/ranked.ts), the
// client and the tests.
//
// - Trophies: a win +30 and a loss −25 against an equal opponent, scaled by the trophy gap the Elo way: with
//   E = 1 ÷ (1 + 10^((theirs − yours) ÷ 400)), a win gives 60 × (1 − E) and a loss costs 50 × E, at least 1 either way.
// - Leagues by trophies: Bronze 0, Silver 400, Gold 800, Platinum 1,200, Mythic 1,600; Legend is ladder places 1-100
//   at Mythic trophies. Once a league is reached in a season, its start is a floor: trophies never drop below it.
// - Matchmaking: the Arena's async saved line-up matching (nearest trophies, a window that widens with waiting). From
//   Lv 40; trainers under Lv 150 meet only each other (and those at 150+ only each other). Level compression as in
//   the Arena.
// - Seasons run 6 weeks and end on a Sunday 03:00 UTC (or when an admin ends one). At the end: the Arena's top prizes
//   (same diamonds, XP boost and tickets) once to the top places, a Legend-only title, then a soft reset: trophies
//   above 1,000 come halfway down to it.
// - Each season has one theme rule from THEMES: a featured region, whose synergy is stronger in ranked fights
//   (shared/synergy.ts SYNERGY.featured: +5% on its perk at each step, Speed +10%), or an element banned from line-ups
//   (those creatures sit out).
// - League milestones pay once a season, cosmetics only (never diamonds): Silver a title, Gold a card frame and 6 h of
//   Aura Charm, Platinum an emote and 6 h of Catch Charm, Mythic the season outfit. The ranked cosmetics are their own
//   family (ids start ranked-), apart from the season pass.
// - Earned charms: every 10 ranked wins that gave trophies pay 1 h of an Aura Charm or a Catch Charm, in turn, at
//   most 3 h a week. All charms are Bag items (shared/items.ts charmItem), tradeable on the market like any other item
//   until used; using one starts or extends its timed boost (shared/catchcharm.ts useCharmItem).
// - Charm supply (economy audit 2026-10-20): 5 h a week and 24 h milestones were sized as if every player hunted 24 h a
//   day; at the hours players really keep the game open (sim/economy.ts PLAYER_MIX) they added about 12% to the
//   server's Aura supply against the design's +5% ceiling (sim/targets.ts auraRankedAll). 3 h and 6 h bring it back.
import type { Element } from './elements.ts';
import { ELEMENTS } from './elements.ts';
import type { RegionId } from './species.ts';
import { SPECIES } from './species.ts';
import { SYNERGY, regionPct, type SynergyOpts, type SynergyScale } from './synergy.ts';
import { seasonStart } from './arena.ts';
import { ITEM, charmItem } from './items.ts';

export const RANKED = {
  unlockLevel: 40,
  /** Trainers under this level are matched only with each other. */
  bracketLevel: 150,
  /** Trophies for a win and a loss against an equal opponent. */
  win: 30,
  loss: 25,
  /** The Elo gap scale. */
  gapScale: 400,
  seasonWeeks: 6,
  /** Soft reset: trophies above `resetLine` keep this share of what is above it. */
  resetLine: 1000,
  resetKeep: 0.5,
  /** Ladder places that are Legend (at Mythic trophies), and the places the page lists. */
  legendSlots: 100,
  ladderShown: 100,
  /** Prizes go to the top places among those with this many matches that season and one in its last prizeActiveMs. */
  prizeMinMatches: 10,
  prizeActiveMs: 3 * 86_400_000,
  /** Earned charms: every `winsPer` counted wins pay `hoursPer` hours, at most `weeklyHours` a week (Sunday 03:00 UTC weeks). */
  charm: { winsPer: 10, hoursPer: 1, weeklyHours: 3, goldAuraHours: 6, platinumCatchHours: 6 },
  /**
   * Starting trophies when ranked takes over from the weekly Arena (server/ranked.ts goLive): Arena rating × 4/3, which
   * lays the AR tiers on the leagues (Silver 300 → 400, Gold 600 → 800, Platinum 900 → 1,200, Diamond 1,200 → Mythic
   * 1,600). The rating is the better of the running week's and the week before's final one.
   */
  seedPerAr: 4 / 3,
} as const;

// ---------- leagues ----------
export type LeagueId = 'bronze' | 'silver' | 'gold' | 'platinum' | 'mythic' | 'legend';
export const LEAGUES: { id: Exclude<LeagueId, 'legend'>; name: string; min: number }[] = [
  { id: 'bronze', name: 'Bronze', min: 0 },
  { id: 'silver', name: 'Silver', min: 400 },
  { id: 'gold', name: 'Gold', min: 800 },
  { id: 'platinum', name: 'Platinum', min: 1200 },
  { id: 'mythic', name: 'Mythic', min: 1600 },
];
export const LEAGUE_NAME: Record<LeagueId, string> = { bronze: 'Bronze', silver: 'Silver', gold: 'Gold', platinum: 'Platinum', mythic: 'Mythic', legend: 'Legend' };
const MYTHIC = LEAGUES[LEAGUES.length - 1].min;

export interface LeagueInfo { id: LeagueId; name: string; /** Trophies where this league starts (Legend: Mythic's). */ min: number }

/** The trophy league (Bronze to Mythic) for a count, ignoring Legend. */
export function trophyLeague(trophies: number): LeagueInfo {
  let l = LEAGUES[0];
  for (const x of LEAGUES) if (trophies >= x.min) l = x;
  return { id: l.id, name: l.name, min: l.min };
}

/** The league shown for trophies and a ladder place (0 = not on the ladder): Legend is places 1-100 at Mythic trophies. */
export function leagueOf(trophies: number, rank = 0): LeagueInfo {
  if (trophies >= MYTHIC && rank >= 1 && rank <= RANKED.legendSlots) return { id: 'legend', name: 'Legend', min: MYTHIC };
  return trophyLeague(trophies);
}

/** The start of the trophy league these trophies are in: the floor once it is reached. */
export const leagueFloor = (trophies: number) => trophyLeague(trophies).min;

/** The next trophy league above these trophies, or null at Mythic. */
export function nextLeague(trophies: number): (typeof LEAGUES)[number] | null {
  return LEAGUES.find((x) => x.min > trophies) ?? null;
}

// ---------- trophies ----------
/** Expected score of a player at `mine` against one at `theirs` (Elo). */
export function expectedScore(mine: number, theirs: number): number {
  return 1 / (1 + Math.pow(10, (theirs - mine) / RANKED.gapScale));
}

/** Trophies a result moves: +30 / −25 against an equal opponent, more or less by the gap, never 0. */
export function trophyDelta(mine: number, theirs: number, win: boolean): number {
  const e = expectedScore(mine, theirs);
  return win ? Math.max(1, Math.round(2 * RANKED.win * (1 - e))) : -Math.max(1, Math.round(2 * RANKED.loss * e));
}

// ---------- strength-aware matching ----------
/**
 * Strength-aware matching (2026-10-15, Cole: "low rank detection so people with one insane creature aren't slowly
 * rising and stomping"). Matches and trophy changes use a match rating instead of bare trophies: a trainer's trophies,
 * or, when higher, the trophies of the players their line-up clearly out-powers (Arena Power at least `clearly` times
 * theirs: 5% stronger wins 94% of even fights, battle-consistency). A strong newcomer meets teams close to its own from
 * the first match, its opponents win and lose the usual amounts, and while its trophies sit below its rating each win
 * pays at least `catchUp` of the gap, so it reaches its league in a handful of wins instead of beating weaker players
 * all the way up. Admin switch kv 'ranked-strength', on by default; off, ratings are trophies.
 * Simulated (200 trainers, a newcomer one Aura above the top 10%): wins over 10%-weaker line-ups on the way up 54 -> 0,
 * over 5%-weaker 57 -> 10. ×1.03 placed a line-up without synergies or held items too high (32% wins once settled);
 * ×1.05 leaves it at 42%.
 */
export const STRENGTH = {
  clearly: 1.05,
  catchUp: 1 / 2,
  /** Power counts once a bracket has this many trainers with a standing this season (trophies or a match). */
  minPool: 8,
} as const;

/** A bracket's Powers and trophies, each sorted low to high: what ratings are placed against. */
export interface StrengthPool { powers: number[]; trophies: number[] }

export function strengthPool(players: readonly { power: number; trophies: number }[]): StrengthPool {
  return { powers: players.map((p) => p.power).sort((a, b) => a - b), trophies: players.map((p) => p.trophies).sort((a, b) => a - b) };
}

/**
 * The trophies a line-up of this Power is placed at: if it clearly out-powers k trainers of the pool, the k-th lowest
 * trophies there. 0 while the pool is under minPool or the line-up out-powers no one.
 */
export function strengthTrophies(pool: StrengthPool, power: number): number {
  if (pool.powers.length < STRENGTH.minPool || !(power > 0)) return 0;
  const max = power / STRENGTH.clearly;
  let lo = 0, hi = pool.powers.length;
  while (lo < hi) { const mid = (lo + hi) >> 1; if (pool.powers[mid] <= max) lo = mid + 1; else hi = mid; }
  return lo ? pool.trophies[lo - 1] : 0;
}

/** A win's trophies while trophies sit below the match rating: at least `catchUp` of the gap. Losses are unchanged. */
export function catchUpWin(delta: number, trophies: number, rating: number): number {
  return delta > 0 && rating > trophies ? Math.max(delta, Math.round((rating - trophies) * STRENGTH.catchUp)) : delta;
}

/** The ranked record a trainer carries through a season. */
export interface RankedStanding {
  trophies: number;
  /** Highest league start reached this season: trophies never drop below it. */
  floor: number;
  matches: number;
  wins: number;
  losses: number;
}

export const freshRanked = (): RankedStanding => ({ trophies: 0, floor: 0, matches: 0, wins: 0, losses: 0 });

/** Apply one result: trophies move by `delta` but never below the floor, and a newly reached league raises the floor. */
export function applyTrophies(s: RankedStanding, delta: number, win: boolean): RankedStanding {
  const trophies = Math.max(s.floor, 0, s.trophies + delta);
  return {
    trophies, floor: Math.max(s.floor, leagueFloor(trophies)),
    matches: s.matches + 1, wins: s.wins + (win ? 1 : 0), losses: s.losses + (win ? 0 : 1),
  };
}

/** A standing seeded from an Arena rating (RANKED.seedPerAr): its league counts as reached, so its start is the floor. */
export function seedStanding(ar: number): RankedStanding {
  const trophies = Math.round(Math.max(0, ar) * RANKED.seedPerAr);
  return { ...freshRanked(), trophies, floor: leagueFloor(trophies) };
}

/** The season-end soft reset: trophies above 1,000 come halfway down to it; the rest stay. */
export function softReset(trophies: number): number {
  return trophies > RANKED.resetLine ? RANKED.resetLine + Math.floor((trophies - RANKED.resetLine) * RANKED.resetKeep) : trophies;
}

/** A standing after the soft reset: the new season's floor is the start of the league the trophies land in. */
export function resetStanding(s: Pick<RankedStanding, 'trophies'>): RankedStanding {
  const trophies = softReset(s.trophies);
  return { trophies, floor: leagueFloor(trophies), matches: 0, wins: 0, losses: 0 };
}

/** Two trainers may meet only on the same side of Lv 150. */
export const sameBracket = (levelA: number, levelB: number) => (levelA < RANKED.bracketLevel) === (levelB < RANKED.bracketLevel);

// ---------- seasons ----------
export type ThemeRule = { k: 'region'; region: RegionId } | { k: 'ban'; element: Element };
export interface Theme { id: string; name: string; rule: ThemeRule }

const region = (id: RegionId, name: string): Theme => ({ id, name: `Featured region: ${name}`, rule: { k: 'region', region: id } });
const ban = (element: Element): Theme => ({ id: `ban-${element}`, name: `Element ban: ${ELEMENTS[element].name}`, rule: { k: 'ban', element } });
/** One theme rule a season, in turn (an admin can set another). Midgard first: every trainer from Lv 40 has some. */
export const THEMES: Theme[] = [region('midgard', 'Midgard'), ban('flame'), region('hellas', 'Hellas'), ban('tide'), region('yomi', 'Yomi'), ban('shade')];
export const THEME: Record<string, Theme> = Object.fromEntries(THEMES.map((t) => [t.id, t]));
export const themeFor = (no: number): Theme => THEMES[(Math.max(1, no) - 1) % THEMES.length];

/**
 * The rule in a few words, as the page shows it: "Midgard synergy HP +8% / +10% (2 / 4 Midgard creatures)". scale: the
 * fight's synergy scale (the board's, shared/pvpfight.ts synergyScaleOf), so the numbers are the ones that apply.
 */
export function themeEffect(t: Theme, scale?: SynergyScale): string {
  if (t.rule.k === 'ban') return `No ${ELEMENTS[t.rule.element].name} creatures in line-ups`;
  const def = SYNERGY.region[t.rule.region], name = t.name.replace('Featured region: ', '');
  const perk = { hp: 'HP', dmg: 'damage', swift: 'Speed' }[def.perk];
  const [lo, hi] = regionPct(t.rule.region, true, scale);
  return `${name} synergy ${perk} +${lo}% / +${hi}% (${SYNERGY.regionAt[0]} / ${SYNERGY.regionAt[1]} ${name} creatures)`;
}

/** Whether a species may fight under the theme (an element ban sits its creatures out). */
export function themeAllows(t: Theme, species: string): boolean {
  return t.rule.k !== 'ban' || !(SPECIES[species]?.elements ?? []).includes(t.rule.element);
}

/** The synergy options a ranked fight runs with under the theme: a featured region's synergy is stronger. */
export function themeSynergy(t: Theme): SynergyOpts {
  return t.rule.k === 'region' ? { featuredRegion: t.rule.region } : {};
}

/** The running season, kept by the server (kv 'ranked-season'). */
export interface RankedSeason { no: number; start: number; end: number; theme: string }

const WEEK = 7 * 86_400_000;
/** A season started at `now` ends on the Sunday 03:00 UTC between 5 and 6 weeks later. */
export function seasonEndFrom(now: number): number {
  return seasonStart(now) + RANKED.seasonWeeks * WEEK;
}
export const newSeason = (no: number, now: number): RankedSeason => ({ no, start: now, end: seasonEndFrom(now), theme: themeFor(no).id });
/** The season's id on stored matches. */
export const seasonTag = (no: number) => `R${no}`;

// ---------- cosmetics ----------
/** One season's ranked cosmetics (the sword-and-laurel family). Later seasons use the last entry until theirs is drawn. */
export interface RankedArt { no: number; name: string; title: string; legendTitle: string; frame: string; emote: string; outfit: string }
export const RANKED_ART: RankedArt[] = [
  { no: 1, name: 'Laurel', title: 'Laurel Duelist', legendTitle: 'Laurel Legend', frame: 'ranked-s1', emote: 'ranked-salute', outfit: 'ranked-s1' },
];
export const rankedArt = (no: number): RankedArt => RANKED_ART.find((a) => a.no === no) ?? RANKED_ART[RANKED_ART.length - 1];

/** League milestones: what reaching each league pays, once a season. Cosmetics and charm items only, never diamonds. */
export type MilestoneLeague = 'silver' | 'gold' | 'platinum' | 'mythic';
export const MILESTONE_LEAGUES: MilestoneLeague[] = ['silver', 'gold', 'platinum', 'mythic'];
export type MilestoneReward =
  | { k: 'title'; id: string }
  | { k: 'frame'; id: string }
  | { k: 'emote'; id: string }
  | { k: 'outfit'; id: string }
  | { k: 'auraCharm'; hours: number }
  | { k: 'catchCharm'; hours: number };

export function milestoneRewards(league: MilestoneLeague, no: number): MilestoneReward[] {
  const a = rankedArt(no);
  switch (league) {
    case 'silver': return [{ k: 'title', id: a.title }];
    case 'gold': return [{ k: 'frame', id: a.frame }, { k: 'auraCharm', hours: RANKED.charm.goldAuraHours }];
    case 'platinum': return [{ k: 'emote', id: a.emote }, { k: 'catchCharm', hours: RANKED.charm.platinumCatchHours }];
    case 'mythic': return [{ k: 'outfit', id: a.outfit }];
  }
}

/**
 * The Bag items a charm reward pays (shared/items.ts charmItem): one 24 h charm, else that many 1 h charms. Ordinary
 * tradeable items until used (shared/catchcharm.ts useCharmItem).
 */
export function charmItems(r: MilestoneReward): { item: string; qty: number } | null {
  if (r.k !== 'auraCharm' && r.k !== 'catchCharm') return null;
  const charm = r.k === 'auraCharm' ? 'aura' : 'catch';
  return r.hours === 24 ? { item: charmItem(charm, 24), qty: 1 } : { item: charmItem(charm, 1), qty: r.hours };
}
/** "Aura Charm (24 h)", "2 × Catch Charm (1 h)". */
export function charmItemsText(r: MilestoneReward): string {
  const x = charmItems(r);
  if (!x) return '';
  return x.qty === 1 ? ITEM[x.item].name : `${x.qty} × ${ITEM[x.item].name}`;
}

/** Milestones reached (floor at or above their league's start) and not yet paid this season, lowest first. */
export function milestonesDue(floor: number, paid: readonly string[]): MilestoneLeague[] {
  return MILESTONE_LEAGUES.filter((id) => floor >= LEAGUES.find((x) => x.id === id)!.min && !paid.includes(id));
}

// ---------- earned charms ----------
export type EarnedCharm = 'aura' | 'catch';
/** Earned-charm progress: counted wins, the kind the next hour pays, and hours paid in the week `week`. */
export interface CharmTrack { wins: number; next: EarnedCharm; week: string; hours: number }
export const freshTrack = (): CharmTrack => ({ wins: 0, next: 'aura', week: '', hours: 0 });

/**
 * One counted win (a ranked win that gave trophies) in week `week`: returns the new track and the charm hour it pays,
 * if any. Every 10th win pays 1 h, Aura then Catch in turn; past 3 h in the week the 10th win pays nothing (the count
 * still starts over).
 */
export function charmWin(t: CharmTrack, week: string): { track: CharmTrack; pay: EarnedCharm | null } {
  const c = RANKED.charm;
  const hours = t.week === week ? t.hours : 0;
  const wins = t.wins + 1;
  if (wins < c.winsPer) return { track: { ...t, wins, week, hours }, pay: null };
  if (hours + c.hoursPer > c.weeklyHours) return { track: { ...t, wins: 0, week, hours }, pay: null };
  return { track: { wins: 0, next: t.next === 'aura' ? 'catch' : 'aura', week, hours: hours + c.hoursPer }, pay: t.next };
}

/** Earned-charm hours a trainer could get at most in a season: the weekly cap every week plus both milestone charms. */
export function maxCharmHoursPerSeason(): { aura: number; catch: number } {
  const weekly = RANKED.charm.weeklyHours * RANKED.seasonWeeks;
  return { aura: weekly / 2 + RANKED.charm.goldAuraHours, catch: weekly / 2 + RANKED.charm.platinumCatchHours };
}

// ---------- prizes ----------
export interface RankedLadderEntry { account: number; trophies: number; at: number; lastPlayed: number; matches: number }

/** Ladder order: trophies, then whoever reached them first. Only trainers with a match this season. */
export function rankedOrder<T extends RankedLadderEntry>(rows: T[]): T[] {
  return rows.filter((r) => r.matches > 0).sort((a, b) => b.trophies - a.trophies || a.at - b.at || a.account - b.account);
}

/** Whether a row can take a top prize: enough matches that season and one in its last 3 days. */
export function rankedPrizeEligible(r: RankedLadderEntry, end: number): boolean {
  return r.matches >= RANKED.prizeMinMatches && r.lastPlayed >= end - RANKED.prizeActiveMs;
}

// ---------- messages ----------
export interface RankedRow { rank: number; name: string; trophies: number; league: LeagueId; outfit: string; w: number; l: number; me?: boolean }
export interface RankedDTO {
  season: { no: number; name: string; endsAt: number; theme: Theme };
  me: {
    trophies: number; league: LeagueInfo; rank: number; floor: number; matches: number; wins: number; losses: number;
    /** Milestones paid this season. */
    paid: MilestoneLeague[];
    /** Earned charms: wins toward the next hour, its kind, and hours paid this week. */
    charm: { wins: number; next: EarnedCharm; hours: number };
  };
  ladder: RankedRow[];
  /** Your row when you are below the shown ladder. */
  mine?: RankedRow;
  notice?: { season: number; rank: number; diamonds: number; xpHours: number; tickets?: number; legend?: string };
  /** Strength-aware matching is on (STRENGTH). */
  strength?: boolean;
}
